import { BetaIcon } from '../../../../lib/components';
import {
  Badge,
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  Group,
  Select,
  Text,
} from '@mantine/core';
import { uniqBy } from 'lodash';
import { useSuppliers } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { Component, TransactionFormData } from '../../../shared/types';
import classes from '../../../shared/components/Inputs/Inputs.module.css';
import { useFormContext } from 'react-hook-form';

type SupplierComboboxItem = ComboboxItem & { status: string };

const SupplierOption = ({
  option,
  checked,
}: ComboboxLikeRenderOptionInput<SupplierComboboxItem>) => {
  return (
    <Group gap="8px" w="100%">
      {checked && <BetaIcon icon="check" size="xs" />}
      <Text size="sm">{option.label}</Text>
      <Badge
        color="dark.8"
        size="md"
        radius="xs"
        style={{ marginLeft: 'auto' }}
      >
        {option.status}
      </Badge>
    </Group>
  );
};

type SelectSupplierProps = {
  component: Component;
  field: {
    value: Component[];
    onChange: (value: Component[]) => void;
  };
  error?: string;
};

const SelectSupplier = ({ component, field, error }: SelectSupplierProps) => {
  const { data: suppliers } = useSuppliers();
  const { t } = useTranslation();
  const form = useFormContext<TransactionFormData>();

  const componentSupplierOptions =
    suppliers
      ?.filter((supplier) =>
        supplier.plotsCommodities.includes(component?.commodity ?? '')
      )
      ?.map((supplier) => ({
        label: supplier.targetBusiness.name || '',
        value: supplier.targetBusiness.id || '',
        status: t(
          `suppliers.riskAssessment.status.${
            supplier?.riskAssessment?.status ?? 'NEW'
          }`
        ),
      }))
      ?.filter(
        (item) => item.label !== undefined && item.value !== undefined
      ) || [];

  const selectedSupplier = componentSupplierOptions.find(
    (s) => s.value === component?.supplierBusiness?.id
  );

  const disabled =
    !!form.watch('importedDds')?.length || form.formState.disabled;

  return (
    <Select
      label={t('common.roles.supplier')}
      placeholder={t('transactions.form.supplierPlaceholder') || ''}
      value={component?.supplierBusiness?.id || ''}
      disabled={disabled}
      onChange={(value) => {
        if (field.value) {
          const newComponents = [...field.value];
          const foundComponent = newComponents.find(
            (nc: Component) => nc.hsn === component.hsn
          );
          if (foundComponent) {
            foundComponent.supplierBusiness.id = value || '';
            field.onChange(newComponents);
          }
        }
      }}
      data={uniqBy(componentSupplierOptions, 'value')}
      inputContainer={(children) => {
        return <div>{children}</div>;
      }}
      renderOption={(o) => (
        <SupplierOption
          option={o.option as SupplierComboboxItem}
          checked={o.checked}
        />
      )}
      rightSectionWidth={'calc-size(fit-content, size + 12px)'}
      rightSection={
        selectedSupplier?.status && (
          <Badge color="dark.8" size="md" radius="xs">
            {selectedSupplier?.status}
          </Badge>
        )
      }
      style={{ flex: 1 }}
      classNames={{
        input: classes.darkBackground,
        dropdown: classes.darkBackground,
      }}
      error={error}
    />
  );
};

export default SelectSupplier;
